import React from 'react'
import { Link } from 'react-router-dom'
import { Modal, Table, Tag, Spin, Alert, Button } from 'antd'

import { useGetBoqFiles } from '../../Boq/hooks'
import apiConfig from '../../../../config/apiConfig'

// Read-only BOQ detail for the Customer Detail dashboard. The full BOQ
// workflow (upload / add items to order) lives on the inquiry items page —
// this modal only inspects an existing BOQ via the same useGetBoqFiles hook.
// Antd portals the modal outside CustomerDetailWrapper, so styles are local.

const Secondary = ({ children }) => (
    <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>{children}</span>
)

const STATUS_COLORS = {
    completed: 'green',
    success: 'green',
    done: 'green',
    processing: 'blue',
    pending: 'orange',
    failed: 'red',
    error: 'red'
}

const statusTag = (status) => (
    <Tag color={STATUS_COLORS[(status || '').toLowerCase()] || 'default'} style={{ margin: 0 }}>
        {status || '-'}
    </Tag>
)

const inferenceCell = (inference) => {
    if (!inference || !inference.item) return <Secondary>-</Secondary>
    const { qty, unitConversionRemark, item } = inference
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {item.url
                ? <a href={apiConfig.apiBase + item.url} target="_blank" rel="noopener noreferrer">{item.name}</a>
                : <span>{item.name}</span>}
            <Secondary>
                {qty} {item.unit || ''}{unitConversionRemark ? ` · ${unitConversionRemark}` : ''}
            </Secondary>
        </div>
    )
}

const itemColumns = [
    {
        title: 'รายการใน BOQ',
        key: 'name',
        render: (it) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span>{it.name}</span>
                {it.searchQuery && it.searchQuery !== it.name && (
                    <Secondary>ค้นหา: {it.searchQuery}</Secondary>
                )}
            </div>
        )
    },
    {
        title: 'จำนวน',
        key: 'qty',
        width: 110,
        render: (it) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{it.qty} {it.unit || ''}</span>
    },
    {
        title: 'หมายเหตุ',
        dataIndex: 'remark',
        width: 160,
        ellipsis: true,
        render: (v) => v || <Secondary>-</Secondary>
    },
    {
        title: 'สินค้าที่จับคู่',
        key: 'closest',
        render: (it) => inferenceCell(it.boqItemClosestInference)
    }
]

const alternativeColumns = [
    {
        title: 'ตัวเลือกอื่น',
        key: 'alt',
        render: (inf) => inferenceCell(inf)
    }
]

const BoqFilePreview = ({ file }) => {
    if (file.contentType === 'text') {
        return <div style={{ whiteSpace: 'pre-line', padding: '8px 0' }}>{file.value}</div>
    }
    return (
        <a href={file.value} target="_blank" rel="noopener noreferrer">
            <img src={file.value} alt={file.filename || 'BOQ file'} style={{ maxWidth: 120, height: 'auto' }} />
        </a>
    )
}

const BoqDetailModal = ({ boq, onClose }) => {
    const boqId = boq && boq.id
    const { loading, error, boqContext, boqFiles } = useGetBoqFiles(boqId)

    return (
        <Modal
            visible={!!boq}
            onCancel={onClose}
            width="80%"
            title={boq ? `BOQ #${boq.id} · ออเดอร์ ${boq.orderNumber}` : 'BOQ'}
            footer={[
                boq && (
                    <Link to={`/inquiries/${boq.orderNumber}/items`} key="manage">
                        <Button type="primary">จัดการ BOQ ที่หน้าออเดอร์</Button>
                    </Link>
                ),
                <Button key="close" onClick={onClose}>ปิด</Button>
            ]}
        >
            {loading && <Spin style={{ display: 'block', margin: '40px auto' }} />}
            {error && <Alert type="error" message={error.message} />}
            {!loading && !error && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {boqContext && (
                        <div>
                            <Secondary>บริบท BOQ</Secondary>
                            <div style={{ whiteSpace: 'pre-line' }}>{boqContext}</div>
                        </div>
                    )}
                    {(!boqFiles || boqFiles.length === 0) && (
                        <Secondary>ยังไม่มีไฟล์ในรายการ BOQ นี้</Secondary>
                    )}
                    {(boqFiles || []).map((file) => (
                        <div key={file.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <strong>{file.filename || `ไฟล์ ${file.id}`}</strong>
                                {statusTag(file.processStatus)}
                            </div>
                            {file.errorMessage && <Alert type="error" message={file.errorMessage} />}
                            <BoqFilePreview file={file} />
                            <Table
                                columns={itemColumns}
                                dataSource={file.boqItems}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                expandable={{
                                    rowExpandable: (it) =>
                                        it.boqItemAlternativeInferences && it.boqItemAlternativeInferences.length > 0,
                                    expandedRowRender: (it) => (
                                        <Table
                                            columns={alternativeColumns}
                                            dataSource={it.boqItemAlternativeInferences}
                                            rowKey="id"
                                            size="small"
                                            pagination={false}
                                        />
                                    )
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    )
}

export default BoqDetailModal
